import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useParams } from "next/navigation";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import LocaleLink from "@/lib/i18n/locale-link";
import { cn, formatPrice, getCurrencySymbol } from "@/lib/utils";
import { getCurrentLangInClient, getValueFromTranslations, useLocaleRouter } from "@/lib/utils.client";
import { type OrderDetailItem, OrderStatusEnum, type UserOrder } from "@/modules/order/schema";
import { useTracking } from "@/track/tracking-hooks";
import { isBagSpuId } from "../utils";
import { ListOrderStatusStoreGo } from "./order-status-store-go";

// 组件 props 类型
interface OrderItemProps {
  order: UserOrder;
  onCancel?: (orderNumber: string) => void;
  cancelPending?: boolean;
  onOrderExpired?: () => void;
  onShowPickupCode?: (pickupCode: string, orderNumber: string, queueCode?: string) => void;
}

// 商品聚合类型
interface SkuItem {
  count: number;
  skuId: string;
  spuId: string;
  sku: NonNullable<OrderDetailItem["sku"]>;
  spu: NonNullable<OrderDetailItem["spu"]>;
}

// 包装器组件 Props
interface UserOrderItemWrapperProps {
  children: React.ReactNode;
  onViewDetail?: () => void;
}

// 操作按钮组件 Props
interface UserOrderItemActionProps {
  order: UserOrder;
  onCancel?: () => void;
  onPay?: () => void;
  onViewDetail?: () => void;
  onShowPickupCode?: () => void;
}

// 包装器组件
const UserOrderItemWrapper: React.FC<UserOrderItemWrapperProps> = t0 => {
  const $ = _c(7);
  const {
    children,
    onViewDetail
  } = t0;
  const isMobile = useIsMobile();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("bg-white no-border md:border pt-4 pb-2.5 md:pt-6 md:pb-6");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isMobile || $[2] !== onViewDetail) {
    t2 = () => {
      if (isMobile) {
        onViewDetail?.();
      }
    };
    $[1] = isMobile;
    $[2] = onViewDetail;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <div className={t1} onClick={t2}>{children}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const UserOrderItemProducts = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="mt-2.5 md:mt-6 flex flex-row items-center justify-between px-4.5 md:px-6 ">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const UserOrderItemSkuList = t0 => {
  const $ = _c(8);
  const {
    skuItems
  } = t0;
  const lang = getCurrentLangInClient();
  const {
    t
  } = useTranslation();
  useParams();
  const isMobile = useIsMobile();
  const skuImageSize = isMobile ? 80 : 118;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("md:overflow-x-auto  ");
    t2 = cn("overflow-x-auto flex gap-2", "md:gap-3  md:w-114");
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const t4 = skuItems.map(t3 => {
    const {
      skuId,
      spuId,
      sku,
      spu
    } = t3;
    return <div key={skuId} className={cn("flex-shrink-0", "md:flex-shrink-0")} onClick={_temp}><LocaleLink href={`/store-pickup/${spuId}`} className="flex   items-center  flex-shrink-0"><Image src={sku.mainImage || ""} alt={getValueFromTranslations(lang, sku.name_trans) || getValueFromTranslations(lang, spu.name_trans) || t("order.item.productImageAlt")} width={skuImageSize} height={skuImageSize} className={cn("object-contain border border-[#eee]  ", "size-20 md:size-[7.375rem]")} /></LocaleLink></div>;
  });
  let t5;
  if ($[2] !== t2 || $[3] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[2] = t2;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t1 || $[6] !== t5) {
    t6 = <div className={t1}>{t5}</div>;
    $[5] = t1;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
};

// 操作按钮组件
const UserOrderItemAction: React.FC<UserOrderItemActionProps> = t0 => {
  const $ = _c(23);
  const {
    order,
    onCancel,
    onPay,
    onViewDetail,
    onShowPickupCode
  } = t0;
  const {
    t
  } = useTranslation();
  const isMobile = useIsMobile();
  const showPickupCodeButton = order.status === OrderStatusEnum.SENDED;
  let t1;
  if ($[0] !== onPay || $[1] !== order.status || $[2] !== t) {
    t1 = order.status === OrderStatusEnum.WAIT_PAY && <Button size="sm" variant="default" className={cn("   ", "w-25 md:w-33 h-8.5 ")} onClick={onPay}>{t("order.item.payNowButton")}</Button>;
    $[0] = onPay;
    $[1] = order.status;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isMobile || $[5] !== onShowPickupCode || $[6] !== showPickupCodeButton || $[7] !== t) {
    t2 = showPickupCodeButton && <Button size="sm" variant={isMobile ? "outline" : "default"} className={cn(" border-black  ", "w-25 md:w-33 h-8.5 ")} onClick={onShowPickupCode}>{t("order.info.pickupCodeButton", "Pickup Code")}</Button>;
    $[4] = isMobile;
    $[5] = onShowPickupCode;
    $[6] = showPickupCodeButton;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== isMobile || $[10] !== onViewDetail || $[11] !== t) {
    t3 = !isMobile ? <Button variant="outline" size="sm" className={cn("  border-border    ", "w-25 md:w-33 h-8.5 ")} onClick={onViewDetail}>{t("order.item.viewDetailsButton")}</Button> : null;
    $[9] = isMobile;
    $[10] = onViewDetail;
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== isMobile || $[14] !== onCancel || $[15] !== order.status || $[16] !== t) {
    t4 = order.status === OrderStatusEnum.WAIT_PAY && <Button size="sm" variant={isMobile ? "outline" : "outline"} className={cn("  border-border   ", "w-25 md:w-33 h-8.5 ")} onClick={onCancel}>{t("order.item.cancelOrderButton")}</Button>;
    $[13] = isMobile;
    $[14] = onCancel;
    $[15] = order.status;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t5 = <div className="mt-2.5 px-4.5 md:px-6 flex flex-row-reverse md:flex-col  items-center gap-3 md:gap-2" onClick={_temp2}>{t1}{t2}{t3}{t4}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
};

// 总金额组件
const UserOrderItemTotal = t0 => {
  const $ = _c(8);
  const {
    quantityText,
    amountText
  } = t0;
  let t1;
  if ($[0] !== quantityText) {
    t1 = <span>{quantityText}</span>;
    $[0] = quantityText;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <br />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== amountText) {
    t3 = <span>{amountText}</span>;
    $[3] = amountText;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <div className=" flex flex-col justify-end items-center font-medium"><div className=" h-8 text-px12 md:text-base text-foreground leading-4 md:leading-none text-right md:text-center">{t1}{t2}{t3}</div></div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const UserOrderItemTitle = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="px-4.5 md:px-6 text-foreground text-px14 md:text-base  whitespace-nowrap leading-none  text-left">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const UserOrderItemAccent = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className=" mt-2.5 px-4.5 text-px10 text-destructive leading-3.25 text-left">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const UserOrderItemStoreInfo = t0 => {
  const $ = _c(10);
  const {
    storeInfo
  } = t0;
  const isMobile = useIsMobile();
  let t1;
  if ($[0] !== storeInfo.name) {
    t1 = <div className="text-foreground  break-words ">{storeInfo.name}</div>;
    $[0] = storeInfo.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isMobile) {
    t2 = isMobile ? null : <Separator orientation="vertical" className="h-6" />;
    $[2] = isMobile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== storeInfo.address) {
    t3 = <div className="text-hint   break-words  ">{storeInfo.address}</div>;
    $[4] = storeInfo.address;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="px-4.5 md:px-6 mt-2.5 md:mt-6 "><div className="bg-muted rounded-px2 md:rounded-px4 leading-none md:leading-5.5  text-px11 md:text-base flex flex-col md:flex-row md:items-center gap-1.5  p-1.25 md:px-6 md:py-4">{t1}{t2}{t3}</div></div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const UserOrderItemStoreGo: React.FC<OrderItemProps> = t0 => {
  const $ = _c(74);
  const {
    order,
    onCancel,
    onShowPickupCode
  } = t0;
  const {
    t
  } = useTranslation();
  const router = useLocaleRouter();
  const isMobile = useIsMobile();
  const {
    locale
  } = useParams();
  const {
    track,
    TrackingEventType
  } = useTracking();
  let t1;
  if ($[0] !== locale || $[1] !== order.orderNumber || $[2] !== router) {
    t1 = function onViewDetail() {
      router.push(`/${locale}/order/${order.orderNumber}`);
    };
    $[0] = locale;
    $[1] = order.orderNumber;
    $[2] = router;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onViewDetail = t1;
  let t2;
  if ($[4] !== onCancel || $[5] !== order.orderNumber) {
    t2 = function handleCancel() {
      onCancel?.(order.orderNumber);
    };
    $[4] = onCancel;
    $[5] = order.orderNumber;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleCancel = t2;
  let t3;
  if ($[7] !== TrackingEventType.CLICK || $[8] !== locale || $[9] !== order.orderNumber || $[10] !== router || $[11] !== track) {
    t3 = function onPay() {
      track("PopMartGlobalWebcontinuePaidClickPay", {
        product_type: "store",
        order_id: order.orderNumber
      }, TrackingEventType.CLICK);
      router.push(`/${locale}/payment?orderNumber=${order.orderNumber}`);
    };
    $[7] = TrackingEventType.CLICK;
    $[8] = locale;
    $[9] = order.orderNumber;
    $[10] = router;
    $[11] = track;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const onPay = t3;
  let t4;
  if ($[13] !== onShowPickupCode || $[14] !== order.orderNumber || $[15] !== order.queueCode || $[16] !== order.verifyCode) {
    t4 = function handleShowPickupCode() {
      const pickupCode = order.verifyCode || "";
      const queueCode = order.queueCode || "";
      onShowPickupCode?.(pickupCode, order.orderNumber, queueCode);
    };
    $[13] = onShowPickupCode;
    $[14] = order.orderNumber;
    $[15] = order.queueCode;
    $[16] = order.verifyCode;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleShowPickupCode = t4;
  let skuItems;
  let storeInfo;
  let t5;
  if ($[18] !== order || $[19] !== t) {
    const quantity = order.items?.filter(_temp3).length || 0;
    let t6;
    if ($[23] !== order.items) {
      const map = new Map();
      order.items?.forEach(item_0 => {
        if (!item_0.sku || !item_0.spu || isBagSpuId(item_0.spuId)) {
          return;
        }
        if (!map.has(item_0.skuId)) {
          map.set(item_0.skuId, {
            count: 1,
            skuId: item_0.skuId,
            spuId: item_0.spuId,
            sku: item_0.sku,
            spu: item_0.spu
          });
        } else {
          const existing = map.get(item_0.skuId);
          if (existing) {
            existing.count = existing.count + 1;
          }
        }
      });
      t6 = Array.from(map.values());
      $[23] = order.items;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    skuItems = t6;
    const t7 = order.store?.name || "";
    const t8 = order.store?.address || "";
    let t9;
    if ($[25] !== t7 || $[26] !== t8) {
      t9 = {
        name: t7,
        address: t8
      };
      $[25] = t7;
      $[26] = t8;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    storeInfo = t9;
    t5 = t("order.item.mobile.totalLabel", {
      quantity,
      currencySymbol: getCurrencySymbol(order.currency),
      amount: formatPrice(order.currency, order.payAmount + (order.freightAmount || 0))
    });
    $[18] = order;
    $[19] = t;
    $[20] = skuItems;
    $[21] = storeInfo;
    $[22] = t5;
  } else {
    skuItems = $[20];
    storeInfo = $[21];
    t5 = $[22];
  }
  const quantityText = t5;
  let t6;
  if ($[28] !== order.currency) {
    t6 = getCurrencySymbol(order.currency);
    $[28] = order.currency;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const t7 = order.payAmount + (order.freightAmount || 0);
  let t8;
  if ($[30] !== order.currency || $[31] !== t7) {
    t8 = formatPrice(order.currency, t7);
    $[30] = order.currency;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const amountText = `${t6}
  ${t8}`;
  let t9;
  if ($[33] !== order) {
    t9 = <span className="md:font-bold"><ListOrderStatusStoreGo order={order} /></span>;
    $[33] = order;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== isMobile) {
    t10 = "" && !isMobile ? <UserOrderItemAccent>{""}</UserOrderItemAccent> : null;
    $[35] = isMobile;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10 || $[38] !== t9) {
    t11 = <UserOrderItemTitle>{t9}{t10}</UserOrderItemTitle>;
    $[37] = t10;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== storeInfo) {
    t12 = storeInfo ? <UserOrderItemStoreInfo storeInfo={storeInfo} /> : null;
    $[40] = storeInfo;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== skuItems) {
    t13 = <UserOrderItemSkuList skuItems={skuItems} />;
    $[42] = skuItems;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== amountText || $[45] !== quantityText) {
    t14 = <UserOrderItemTotal quantityText={quantityText} amountText={amountText} />;
    $[44] = amountText;
    $[45] = quantityText;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== handleCancel || $[48] !== handleShowPickupCode || $[49] !== isMobile || $[50] !== onPay || $[51] !== onViewDetail || $[52] !== order) {
    t15 = isMobile ? null : <UserOrderItemAction order={order} onCancel={handleCancel} onPay={onPay} onViewDetail={onViewDetail} onShowPickupCode={handleShowPickupCode} />;
    $[47] = handleCancel;
    $[48] = handleShowPickupCode;
    $[49] = isMobile;
    $[50] = onPay;
    $[51] = onViewDetail;
    $[52] = order;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== t13 || $[55] !== t14 || $[56] !== t15) {
    t16 = <UserOrderItemProducts>{t13}{t14}{t15}</UserOrderItemProducts>;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== isMobile) {
    t17 = isMobile && "" ? <UserOrderItemAccent>{""}</UserOrderItemAccent> : null;
    $[58] = isMobile;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== handleCancel || $[61] !== handleShowPickupCode || $[62] !== isMobile || $[63] !== onPay || $[64] !== onViewDetail || $[65] !== order) {
    t18 = isMobile ? <UserOrderItemAction order={order} onCancel={handleCancel} onPay={onPay} onViewDetail={onViewDetail} onShowPickupCode={handleShowPickupCode} /> : null;
    $[60] = handleCancel;
    $[61] = handleShowPickupCode;
    $[62] = isMobile;
    $[63] = onPay;
    $[64] = onViewDetail;
    $[65] = order;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== onViewDetail || $[68] !== t11 || $[69] !== t12 || $[70] !== t16 || $[71] !== t17 || $[72] !== t18) {
    t19 = <UserOrderItemWrapper onViewDetail={onViewDetail}>{t11}{t12}{t16}{t17}{t18}</UserOrderItemWrapper>;
    $[67] = onViewDetail;
    $[68] = t11;
    $[69] = t12;
    $[70] = t16;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  return t19;
};
function _temp(e) {
  if (window.innerWidth < 768) {
    e.stopPropagation();
  }
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp3(item) {
  return !isBagSpuId(item.spuId);
}